import { FC, memo, useCallback } from 'react';
import { castArray } from 'lodash';

import { Redirect, useRoute } from '@core/navigation';
import {
  AnalyticsCategory,
  AnalyticsPageName,
  RoutePath,
} from '@common/constants';
import {
  Survey,
  SurveyOnCompleteCallback,
  SurveyOnNextCallback,
  SurveyOnPageChangeCallback,
  SurveyOnUploadFilesCallback,
} from '@wearehelpful/ui-kit';
import { useContextSelector } from 'use-context-selector';
import {
  useUpdateSurvey,
  useGetSurvey,
  useGetSurveysBootstrap,
  useCompleteSurvey,
  useUploadSurveyFiles,
} from '@helpful/network';
import { TrackPageView, useAnalytics } from '@wearehelpful/ui-stats';

import { useNavigateNext } from '../Details.hooks';
import { DetailsContext } from '../Details.context';

import { RouteSearch, RouteParams } from './Questions.types';

export const Questions: FC = memo(() => {
  const { page, track } = useAnalytics();
  const [{ search, params }] = useRoute<RouteParams, RouteSearch>();

  const survey = useContextSelector(DetailsContext, ({ survey }) => survey);

  const [, navigate] = useNavigateNext({
    ...search,
    handleSuccess: true,
  });

  const isSurveyLoading = useContextSelector(
    DetailsContext,
    ({ loading }) => loading
  );

  const { mutate: upload } = useUploadSurveyFiles();

  const { data, isInitialLoading: isSurveyInitialLoading } = useGetSurvey(
    search?.id,
    {},
    {
      query: {
        enabled: !!search.id,
        initialData: {
          data: {
            id: search.id,
            slug: null,
            caregiver: null,
            updatedAt: null,
          },
        },
      },
    }
  );

  const { data: bootstrap, isLoading: isBootstrapLoading } =
    useGetSurveysBootstrap({
      logo: {
        height: 36 * window.devicePixelRatio,
      },
    });

  const { mutateAsync: update } = useUpdateSurvey();

  const { mutateAsync: completeSurvey } = useCompleteSurvey();

  const handleOnComplete: SurveyOnCompleteCallback<{}> = useCallback(
    async ({ data, complete }) => {
      await completeSurvey(
        {
          id: search.id,
          data: {
            data,
            slug: params.slug,
          },
        },
        {
          onSuccess: async () => {
            complete();
            page({
              category: AnalyticsCategory.SURVEY,
              name: AnalyticsPageName.SurveyDetailsQuestionsCompleted,
              properties: {
                slug: params.slug,
                ...data,
              },
            });
            if (survey?.trackEventName) {
              track({
                id: survey?.trackEventName,
                properties: {
                  slug: params.slug,
                  ...data,
                },
              });
            }
            navigate();
          },
        }
      );
    },
    [
      search,
      page,
      track,
      survey?.trackEventName,
      params,
      navigate,
      completeSurvey,
    ]
  );

  const handleOnNext: SurveyOnNextCallback<{}> = useCallback(
    async ({ data: response, complete }) => {
      await update(
        {
          id: search.id,
          data: {
            response,
          },
        },
        {
          onSuccess: () => complete(),
        }
      );
    },
    [update, search]
  );

  const handleOnPageChange: SurveyOnPageChangeCallback<{}> = useCallback(
    ({ pageName, pageNo, pageTitle, data }) => {
      page({
        category: AnalyticsCategory.SURVEY,
        name: AnalyticsPageName.SurveyDetailsQuestionsPages,
        properties: { pageName, pageNo, pageTitle, slug: params.slug, ...data },
      });
    },
    [page, params]
  );

  const handleOnUploadFiles: SurveyOnUploadFilesCallback = useCallback(
    ({ files, name }, callback) => {
      upload(
        {
          id: search.id,
          data: {
            files,
            fieldname: name,
            slug: params.slug,
          },
        },
        {
          onSuccess: () => callback({ files }),
          onError: ({ message }) => callback({ error: castArray(message) }),
        }
      );
    },
    [upload, params, search]
  );

  const loading =
    isSurveyLoading || isSurveyInitialLoading || isBootstrapLoading;

  if (!!survey && !search.id) {
    return (
      <Redirect
        path={RoutePath.SURVEYS_DETAILS}
        params={{ slug: survey?.slug }}
      />
    );
  }

  return (
    <TrackPageView
      category={AnalyticsCategory.SURVEY}
      name={AnalyticsPageName.SurveyDetailsQuestions}
      properties={{ slug: params.slug }}
    >
      <Survey<{}>
        onUploadFiles={handleOnUploadFiles}
        key={`${loading}`}
        initialValues={data.data.response}
        optionsImages={{ insuranceProvider: bootstrap?.logos?.insurance }}
        loading={loading}
        json={survey?.json}
        onNext={handleOnNext}
        onComplete={handleOnComplete}
        onPageChange={handleOnPageChange}
      />
    </TrackPageView>
  );
});
